import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { ACTIVITY_STATUSES, VALIDATION_STATUSES } from "@academichub/shared";
import { listActivities } from "../api/activities";
import { listCategories, listSemesters } from "../api/master";
import { ActivityCard } from "../components/ActivityCard";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Select,
  Spinner,
} from "../components/ui";
import { activityStatusLabel, validationStatusLabel } from "../lib/format";

/** Feed kegiatan dengan filter dan paginasi (status filter tersimpan di URL). */
export function FeedPage() {
  const [params, setParams] = useSearchParams();

  const page = Number(params.get("page") ?? "1");
  const q = params.get("q") ?? "";
  const status = params.get("status") ?? "";
  const validationStatus = params.get("validationStatus") ?? "";
  const categoryId = params.get("categoryId") ?? "";
  const semesterId = params.get("semesterId") ?? "";

  const categoriesQuery = useQuery({ queryKey: ["master", "categories"], queryFn: listCategories });
  const semestersQuery = useQuery({ queryKey: ["master", "semesters"], queryFn: listSemesters });

  const listQuery = useQuery({
    queryKey: ["activities", { page, q, status, validationStatus, categoryId, semesterId }],
    queryFn: () =>
      listActivities({ page, perPage: 10, q, status, validationStatus, categoryId, semesterId }),
  });

  function update(next: Record<string, string>): void {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    if (!("page" in next)) merged.delete("page");
    setParams(merged, { replace: true });
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Kegiatan"
        description="Daftar kegiatan pada lingkup wewenang Anda."
        actions={
          <Link to="/kegiatan/baru">
            <Button>Catat kegiatan</Button>
          </Link>
        }
      />

      <Card className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Cari judul" htmlFor="q">
            <Input
              id="q"
              value={q}
              onChange={(event) => update({ q: event.target.value })}
              placeholder="mis. workshop"
            />
          </Field>

          <Field label="Status kegiatan" htmlFor="status">
            <Select
              id="status"
              value={status}
              onChange={(event) => update({ status: event.target.value })}
            >
              <option value="">Semua status</option>
              {ACTIVITY_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {activityStatusLabel(value)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Status validasi" htmlFor="validationStatus">
            <Select
              id="validationStatus"
              value={validationStatus}
              onChange={(event) => update({ validationStatus: event.target.value })}
            >
              <option value="">Semua validasi</option>
              {VALIDATION_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {validationStatusLabel(value)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Kategori" htmlFor="categoryId">
            <Select
              id="categoryId"
              value={categoryId}
              onChange={(event) => update({ categoryId: event.target.value })}
            >
              <option value="">Semua kategori</option>
              {(categoriesQuery.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Semester" htmlFor="semesterId">
            <Select
              id="semesterId"
              value={semesterId}
              onChange={(event) => update({ semesterId: event.target.value })}
            >
              <option value="">Semua semester</option>
              {(semestersQuery.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Field>

          <div className="flex items-end">
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
            >
              <Search className="size-4" aria-hidden />
              Bersihkan filter
            </Button>
          </div>
        </div>
      </Card>

      {listQuery.isLoading ? <Spinner /> : null}

      {listQuery.isError ? (
        <Alert tone="error">Daftar kegiatan tidak dapat dimuat. Coba lagi sebentar lagi.</Alert>
      ) : null}

      {listQuery.data ? (
        listQuery.data.items.length === 0 ? (
          <EmptyState
            title="Tidak ada kegiatan yang cocok"
            description="Ubah filter atau catat kegiatan baru."
          />
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Menampilkan {listQuery.data.items.length} dari {listQuery.data.total} kegiatan.
            </p>
            <ul className="space-y-3">
              {listQuery.data.items.map((activity) => (
                <li key={activity.id}>
                  <ActivityCard activity={activity} />
                </li>
              ))}
            </ul>
            <Pagination
              page={listQuery.data.page}
              totalPages={listQuery.data.totalPages}
              onChange={(next) => update({ page: String(next) })}
            />
          </div>
        )
      ) : null}
    </div>
  );
}
